import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Typography, Chip, Divider } from '@mui/material'
import { ChevronLeftIcon, CalendarIcon, ClockIcon, UserGroupIcon, UsersIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { LocalTimeBadge } from '../../../shared/components/LocalTimeBadge'
import { TESTIDS } from '../../../config/testids'
import { GlassContainer, Button as DesignSystemButton } from '../../../interaxDesignSystemComponents'
import { useGetUpcomingActivitiesListForGamer } from '../../../apiLayer/queryHooks/queries'
import { INTERAX_COLORS } from '../../../constants/colors'
import { ActivityWithSessions } from '../../../types/GeneralTypes'
import { PageWrapper } from '../../../shared/components'
import { shouldShowGoDashboardButton } from '../helpers/activityHelpers'
import { GamerRoutes } from '../../../config/routes/routeEnums'
import { ActivityType } from '../../../../../pure-js/enums/ActivityType'
import { GameDesktopOnlyModal } from '../components/GameDesktopOnlyModal'
import { useMobileGameGuard } from '../hooks/useMobileGameGuard'

const GAME_ACTIVITY_TYPES = [ActivityType.FIRST_GAME, ActivityType.SHORT_GAME, ActivityType.LONG_GAME] as string[]

/**
 * ActivityDetailsPage - Shows detailed information about a specific activity
 */
export const ActivityDetailsPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const activityId = searchParams.get('id')
  const [activity, setActivity] = useState<ActivityWithSessions | null>(null)

  // Fetch upcoming activities list using React Query hook
  const { data: activitiesResponse, isLoading: loading } = useGetUpcomingActivitiesListForGamer()

  // Find the specific activity based on ID
  useEffect(() => {
    if (activitiesResponse?.data && activityId) {
      const foundActivity = activitiesResponse.data.find((a) => a.id === activityId)
      if (foundActivity) {
        setActivity(foundActivity)
      } else {
        setActivity(null)
      }
    }
  }, [activitiesResponse, activityId])

  const handleBack = () => {
    navigate('/gamer/upcoming-activities')
  }

  const { guardGameEntry, isDesktopOnlyModalOpen, closeDesktopOnlyModal } = useMobileGameGuard()

  const handleJoin = () => {
    // Check if activity is currently live (not ended and within time window)
    const isLive = activity && !activity.endedAt && activity.startDate && activity.endDate
    if (!isLive) return

    // Game activities are desktop-only — blocked on phones/tablets. Non-game types keep the old path.
    if (GAME_ACTIVITY_TYPES.includes(activity.type ?? '')) {
      guardGameEntry(() => {
        navigate('/gamer/play-game-without-introduction')
      })
      return
    }

    navigate('/gamer/play-game-without-introduction')
  }

  // Helper to get status from activity
  const getActivityStatus = (act: ActivityWithSessions): 'live' | 'upcoming' | 'completed' => {
    if (act.endedAt) return 'completed'
    if (act.startDate && act.endDate) {
      const now = new Date()
      const start = new Date(act.startDate)
      const end = new Date(act.endDate)
      if (now >= start && now <= end) return 'live'
    }
    return 'upcoming'
  }

  const activityStatus = activity ? getActivityStatus(activity) : undefined

  // Format date and time from startDate
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  }

  const getDuration = (start: string, end: string) => {
    const startTime = new Date(start).getTime()
    const endTime = new Date(end).getTime()
    const minutes = Math.round((endTime - startTime) / 60000)
    return `${minutes} min`
  }

  if (loading) {
    return (
      <PageWrapper
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: INTERAX_COLORS.typographyFont }}
      >
        Loading activity details...
      </PageWrapper>
    )
  }

  if (!activity) {
    return (
      <PageWrapper
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: INTERAX_COLORS.typographyFont }}
      >
        Activity not found
      </PageWrapper>
    )
  }

  const getStatusColor = (status: 'live' | 'upcoming' | 'completed') => {
    switch (status) {
      case 'live':
        return INTERAX_COLORS.green
      case 'upcoming':
        return INTERAX_COLORS.sun
      case 'completed':
        return INTERAX_COLORS.thunderGrey
    }
  }

  return (
    <PageWrapper>
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0
        }}
      >
        <GlassContainer
          style={{
            width: '100%',
            maxWidth: '900px',
            margin: '0 auto',
            minWidth: 0
          }}
        >
          {/* Back Button */}
          <Box sx={{ mb: 3 }}>
            <DesignSystemButton
              variant="secondary"
              onClick={handleBack}
              icon={<ChevronLeftIcon className="w-5 h-5" />}
              iconPosition="left"
            >
              {t('common.back')}
            </DesignSystemButton>
          </Box>

          {/* Header with Status */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography
                variant="h4"
                sx={{
                  color: INTERAX_COLORS.white,
                  fontWeight: 'bold'
                }}
              >
                {activity.title}
              </Typography>
              {activityStatus && (
                <Chip
                  label={activityStatus.toUpperCase()}
                  sx={{
                    backgroundColor: `${getStatusColor(activityStatus)}40`,
                    color: getStatusColor(activityStatus),
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
              )}
            </Box>
            {activity.sprintTitle && (
              <Typography
                sx={{
                  color: INTERAX_COLORS.thunderGrey,
                  fontSize: '1rem'
                }}
              >
                {activity.sprintTitle}
              </Typography>
            )}
          </Box>

          <Divider sx={{ borderColor: `${INTERAX_COLORS.white}20`, mb: 4 }} />

          {/* Description */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                color: INTERAX_COLORS.white,
                fontWeight: 600,
                mb: 2
              }}
            >
              {t('common.description')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: INTERAX_COLORS.typographyFont,
                lineHeight: 1.6
              }}
            >
              {activity.description}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: `${INTERAX_COLORS.white}20`, mb: 4 }} />

          {/* Activity Details Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr', md: 'repeat(2, 1fr)' },
              gap: 3,
              mb: 4
            }}
          >
            {/* Date & Time */}
            <Box
              sx={{
                p: 3,
                borderRadius: '10px',
                backgroundColor: `${INTERAX_COLORS.heritageBlue}40`,
                border: `1px solid ${INTERAX_COLORS.white}10`
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <CalendarIcon style={{ width: '20px', height: '20px', color: INTERAX_COLORS.sun }} />
                <Typography
                  sx={{
                    color: INTERAX_COLORS.white,
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}
                >
                  {t('common.dateAndTime')}
                </Typography>
              </Box>
              <Typography sx={{ color: INTERAX_COLORS.typographyFont, fontSize: '0.875rem' }}>
                {activity.startDate && formatDate(activity.startDate)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ color: INTERAX_COLORS.typographyFont, fontSize: '0.875rem' }}>
                  {activity.startDate && formatTime(activity.startDate)}
                </Typography>
                <LocalTimeBadge />
              </Box>
            </Box>

            {/* Duration */}
            {activity.startDate && activity.endDate && (
              <Box
                sx={{
                  p: 3,
                  borderRadius: '10px',
                  backgroundColor: `${INTERAX_COLORS.heritageBlue}40`,
                  border: `1px solid ${INTERAX_COLORS.white}10`
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <ClockIcon style={{ width: '20px', height: '20px', color: INTERAX_COLORS.sun }} />
                  <Typography
                    sx={{
                      color: INTERAX_COLORS.white,
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}
                  >
                    {t('common.duration')}
                  </Typography>
                </Box>
                <Typography sx={{ color: INTERAX_COLORS.typographyFont, fontSize: '0.875rem' }}>
                  {getDuration(activity.startDate, activity.endDate)}
                </Typography>
              </Box>
            )}

            {/* Team */}
            {activity.teamIds && activity.teamIds.length > 0 && (
              <Box
                sx={{
                  p: 3,
                  borderRadius: '10px',
                  backgroundColor: `${INTERAX_COLORS.heritageBlue}40`,
                  border: `1px solid ${INTERAX_COLORS.white}10`
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <UserGroupIcon style={{ width: '20px', height: '20px', color: INTERAX_COLORS.sun }} />
                  <Typography
                    sx={{
                      color: INTERAX_COLORS.white,
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}
                  >
                    {t('common.team')}
                  </Typography>
                </Box>
                <Typography sx={{ color: INTERAX_COLORS.typographyFont, fontSize: '0.875rem' }}>
                  {activity.teamIds.length} {activity.teamIds.length === 1 ? 'team' : 'teams'}
                </Typography>
              </Box>
            )}

            {/* Participants */}
            {activity.participants && activity.participants.length > 0 && (
              <Box
                sx={{
                  p: 3,
                  borderRadius: '10px',
                  backgroundColor: `${INTERAX_COLORS.heritageBlue}40`,
                  border: `1px solid ${INTERAX_COLORS.white}10`
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <UsersIcon style={{ width: '20px', height: '20px', color: INTERAX_COLORS.sun }} />
                  <Typography
                    sx={{
                      color: INTERAX_COLORS.white,
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}
                  >
                    {t('common.participants')}
                  </Typography>
                </Box>
                <Typography sx={{ color: INTERAX_COLORS.typographyFont, fontSize: '0.875rem' }}>
                  {activity.participants.length} {activity.participants.length === 1 ? 'participant' : 'participants'}
                </Typography>
              </Box>
            )}

            {/* Activity Type */}
            {activity.type && (
              <Box
                sx={{
                  p: 3,
                  borderRadius: '10px',
                  backgroundColor: `${INTERAX_COLORS.heritageBlue}40`,
                  border: `1px solid ${INTERAX_COLORS.white}10`
                }}
              >
                <Typography
                  sx={{
                    color: INTERAX_COLORS.white,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    mb: 1
                  }}
                >
                  {t('common.type')}
                </Typography>
                <Typography sx={{ color: INTERAX_COLORS.typographyFont, fontSize: '0.875rem' }}>
                  {activity.type.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Action Button */}
          {activityStatus === 'live' && (
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
              <DesignSystemButton variant="primary" onClick={handleJoin} data-testid={TESTIDS.ACTIVITY_JOIN_BTN}>
                {t('gamer.activities.joinNow')}
              </DesignSystemButton>
            </Box>
          )}

          {/* Go Dashboard Button - shows for specific activity types within 5 minutes before start */}
          {activity && shouldShowGoDashboardButton(activity) && (
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <DesignSystemButton variant="primary" onClick={() => navigate(GamerRoutes.DASHBOARD)}>
                {t('gamer.activities.goDashboard')}
              </DesignSystemButton>
            </Box>
          )}
        </GlassContainer>
      </Box>

      <GameDesktopOnlyModal isOpen={isDesktopOnlyModalOpen} onClose={closeDesktopOnlyModal} />
    </PageWrapper>
  )
}
